import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CustomFieldsEditor } from "@/features/servers/components/CustomFieldsEditor";
import { SecretInput } from "@/features/servers/components/SecretInput";
import {
  COMMON_UBUNTU_VERSIONS,
  HOST_KIND_LABELS,
  OS_FAMILY_LABELS,
  type CreateHostInput,
  type HostKind,
  type ICustomField,
  type IHost,
  type OsFamily,
} from "@/features/servers/schemas/servers";
import {
  formatOsLabel,
  parseOsVersion,
  resolveHostOsVersion,
} from "@/features/servers/utils/os-support";

interface HostFormDialogProps {
  open: boolean;
  onClose: () => void;
  host?: IHost | null;
  onSubmit: (data: CreateHostInput) => Promise<void>;
  isSaving?: boolean;
}

const EMPTY: CreateHostInput = {
  name: "",
  kind: "physical",
  ip: "",
  anydeskId: "",
  anydeskPass: "",
  os: "",
  osFamily: "ubuntu",
  osVersion: "",
  osUser: "",
  osPass: "",
  hostname: "",
  hardware: "",
  customFields: [],
};

export function HostFormDialog({
  open,
  onClose,
  host,
  onSubmit,
  isSaving = false,
}: HostFormDialogProps) {
  const [form, setForm] = useState<CreateHostInput>(EMPTY);

  useEffect(() => {
    if (!open) return;
    if (host) {
      const osVersion =
        resolveHostOsVersion(host) ?? host.osVersion ?? "";
      const osFamily =
        host.osFamily ??
        (parseOsVersion(host.os) ? "ubuntu" : host.os ? "other" : "ubuntu");
      setForm({
        name: host.name,
        kind: host.kind,
        ip: host.ip ?? "",
        anydeskId: host.anydeskId ?? "",
        anydeskPass: host.anydeskPass ?? "",
        os: host.os ?? "",
        osFamily,
        osVersion,
        osUser: host.osUser ?? "",
        osPass: host.osPass ?? "",
        hostname: host.hostname ?? "",
        hardware: host.hardware ?? "",
        customFields: host.customFields ?? [],
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, host]);

  const set = <K extends keyof CreateHostInput>(
    key: K,
    value: CreateHostInput[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    const osFamily = form.osFamily ?? "ubuntu";
    const osVersion = form.osVersion?.trim() || undefined;
    const osLabel =
      formatOsLabel(osFamily, osVersion, form.os) || form.os?.trim() || undefined;
    await onSubmit({
      ...form,
      name: form.name.trim(),
      osFamily,
      osVersion,
      os: osLabel,
      customFields: (form.customFields ?? []).filter((f) => f.key.trim()),
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto gap-4">
        <DialogHeader>
          <DialogTitle className="text-base">
            {host ? "Editar host" : "Nuevo host"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Máquina física, VM o estación (típicamente Ubuntu Desktop + AnyDesk).
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="host-name">Nombre</Label>
            <Input
              id="host-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Server RIS/PACS"
            />
          </div>
          <div className="space-y-2">
            <Label>Tipo</Label>
            <Select
              value={form.kind}
              onValueChange={(v) => set("kind", v as HostKind)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(HOST_KIND_LABELS) as HostKind[]).map((k) => (
                  <SelectItem key={k} value={k}>
                    {HOST_KIND_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="host-ip">IP</Label>
            <Input
              id="host-ip"
              value={form.ip ?? ""}
              onChange={(e) => set("ip", e.target.value)}
              placeholder="192.168.20.17"
            />
          </div>
          <div className="space-y-2">
            <Label>Familia SO</Label>
            <Select
              value={form.osFamily ?? "ubuntu"}
              onValueChange={(v) => set("osFamily", v as OsFamily)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(OS_FAMILY_LABELS) as OsFamily[]).map((f) => (
                  <SelectItem key={f} value={f}>
                    {OS_FAMILY_LABELS[f]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="host-os-version">Versión SO</Label>
            <Input
              id="host-os-version"
              list="host-ubuntu-versions"
              value={form.osVersion ?? ""}
              onChange={(e) => set("osVersion", e.target.value)}
              placeholder="20.04"
              className="font-mono"
            />
            <datalist id="host-ubuntu-versions">
              {COMMON_UBUNTU_VERSIONS.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </div>
          <div className="space-y-2">
            <Label htmlFor="host-hostname">Hostname</Label>
            <Input
              id="host-hostname"
              value={form.hostname ?? ""}
              onChange={(e) => set("hostname", e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="host-os-user">Usuario SO</Label>
            <Input
              id="host-os-user"
              value={form.osUser ?? ""}
              onChange={(e) => set("osUser", e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="host-os-pass">Clave SO</Label>
            <SecretInput
              id="host-os-pass"
              value={form.osPass ?? ""}
              onChange={(v) => set("osPass", v)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="host-anydesk-id">AnyDesk ID</Label>
            <Input
              id="host-anydesk-id"
              value={form.anydeskId ?? ""}
              onChange={(e) => set("anydeskId", e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="host-anydesk-pass">AnyDesk clave</Label>
            <SecretInput
              id="host-anydesk-pass"
              value={form.anydeskPass ?? ""}
              onChange={(v) => set("anydeskPass", v)}
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="host-hw">Ram / Disco / Procesador</Label>
            <Input
              id="host-hw"
              value={form.hardware ?? ""}
              onChange={(e) => set("hardware", e.target.value)}
              placeholder="16GB / 500GB / Intel N100"
            />
          </div>
          <div className="sm:col-span-2">
            <CustomFieldsEditor
              fields={(form.customFields as ICustomField[]) ?? []}
              onChange={(fields) => set("customFields", fields)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="cursor-pointer"
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            className="cursor-pointer"
            onClick={() => void handleSave()}
            disabled={isSaving || !form.name.trim()}
          >
            {isSaving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
